import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from "@angular/core";
import { Marketplace } from "@front/m19-api-client";
import { Marketplaces } from "@front/m19-models";
import { IDropdownMenuSeparatorComponent, ISelectMenuImports } from "@front/m19-ui";
import { TranslocoService } from "@jsverse/transloco";
import {
  MarketplaceOption,
  MarketplaceOptionComponent,
} from "@m19-board/marketplace-selector/marketplace-option.component";

@Component({
  selector: "app-marketplace-selector",
  imports: [ISelectMenuImports, IDropdownMenuSeparatorComponent, MarketplaceOptionComponent],
  templateUrl: "./marketplace-selector.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketplaceSelectorComponent {
  private readonly translocoService = inject(TranslocoService);

  public readonly marketplaces = input<Marketplace[]>([]);
  public readonly selectedMarketplace = input<Marketplace | undefined>();
  public readonly selectedMarketplaces = input<Marketplace[]>([]);
  public readonly multiSelection = input(false);

  public readonly marketplaceChange = output<Marketplace | undefined>();
  public readonly marketplacesChange = output<Marketplace[]>();

  public readonly marketplaceOptions = computed<MarketplaceOption[]>(() => {
    return this.marketplaces().map((m) => ({
      marketplace: m,
      name: Marketplaces[m].name,
      translationKey: Marketplaces[m].translationKey,
    }));
  });

  public readonly selectedMarketplaceOption = computed<MarketplaceOption | undefined>(() => {
    return this.marketplaceOptions().find((m) => m.marketplace === this.selectedMarketplace());
  });

  public readonly selectedMarketplacesOptions = computed<MarketplaceOption[]>(() => {
    return this.marketplaceOptions().filter((m) => this.selectedMarketplaces().includes(m.marketplace));
  });

  public readonly selectedMarketplacesLabel = computed<string>(() => {
    const selection = this.selectedMarketplacesOptions();
    if (selection.length === 0) return this.translocoService.translate("common.none");
    if (selection.length === this.marketplaces().length)
      return this.translocoService.translate("marketplace-selector.all_marketplaces");
    return this.translocoService.translate("marketplace-selector.selected_marketplaces", { count: selection.length });
  });

  public readonly searchQuery = signal<string>("");

  public readonly filteredMarketplaceOptions = computed<MarketplaceOption[]>(() => {
    return this.marketplaceOptions().filter((m) => m.name.toLowerCase().includes(this.searchQuery().toLowerCase()));
  });

  public selectMarketplace(marketplace: MarketplaceOption | undefined) {
    if (!marketplace) return;
    this.marketplaceChange.emit(marketplace.marketplace);
  }

  selectMarketplaces(marketplaces: MarketplaceOption[]) {
    this.marketplacesChange.emit(marketplaces.map((m) => m.marketplace));
  }
}
